import { useState, type FC } from 'react';
import cx from 'classnames';
import { Switchbox, Space, InputText, Button } from '@g2u/ui-kit';
import { type FormInstance } from 'antd';
import { useMutation } from '@tanstack/react-query';

import { AddressType, Condition, type JsonDataItem, type WithOptionalDataTest } from 'types';
import { getAddressPrediction, getAddressPredictionDetails, getSuburb } from 'queries';
import { AddressLookup, Section } from 'components';
import { Toast, parseErrors } from 'helpers';
import styles from './billing-address-fields.module.scss';
import { EditOutlined, SearchOutlined } from '@ant-design/icons';

type Props = {
  inline?: boolean;
  form: FormInstance;
  customer: JsonDataItem | null;
  isTechnicianMode?: boolean;
} & WithOptionalDataTest;

export const BillingAddressFields: FC<Props> = (props) => {
  const { inline, customer, isTechnicianMode, form } = props;
  const [isManualEditing, setIsManualEditing] = useState<boolean>(false);
  const isEmptyFieldsHidden = !customer?.physical_address_line1 && !isManualEditing && isTechnicianMode;

  const [isAddressSame, setIsAddressSame] = useState<boolean>(
    customer ? customer?.same_address === Condition.Yes && !isTechnicianMode : true,
  );

  // Address lookup: get prediction list
  const { mutate: predictAddress, data: predictedBillingAddress } = useMutation(getAddressPrediction, {
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  // Address lookup: select prediction and get details
  const handlePredictionSelect = (option: JsonDataItem) => {
    const label = option.label.substring(0, option.label.indexOf(','));
    form.setFieldValue('postalAddressLine1', label);

    getPredictionDetails({ id: option.value, type: AddressType.Postal });
  };
  const { mutate: getPredictionDetails } = useMutation(getAddressPredictionDetails, {
    onSuccess: (data: JsonDataItem) => {
      const addressLine1 = data.address.properties.formatted_address.substring(
        0,
        data.address.properties.formatted_address.indexOf(','),
      );
      const suburb = data.address.properties.locality_name;
      const state = data.address.properties.state_territory;
      const postcode = data.address.properties.postcode;

      form.setFieldsValue({
        postalAddressLine1: addressLine1,
        postalAddressSuburb: suburb,
        postalAddressState: state,
        postalAddressPostcode: postcode,
      });
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  // Suburb lookup
  const { mutate: suburbLookup, data: suggestedSuburbs } = useMutation(getSuburb, {
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  // Suburb lookup: set selection to dependend fields
  const handleSuburbSelect = (option: JsonDataItem) => {
    const selectedSuburb = suggestedSuburbs.find((suggestion: JsonDataItem) => suggestion.ID === option.value);
    form.setFieldsValue({
      postalAddressSuburb: selectedSuburb.suburb,
      postalAddressState: selectedSuburb.state,
      postalAddressPostcode: selectedSuburb.postcode,
    });
  };
  const handleEditingMode = () => {
    // reset fields after manual editing
    if (isManualEditing) {
      form.setFieldValue(
        'postalAddressLine1',
        customer?.postal_address_line1 || customer?.postal_address.postal_address_line_1,
      );
      form.setFieldValue('postalAddressSuburb', customer?.postal_suburb || customer?.postal_address.suburb);
      form.setFieldValue('postalAddressState', customer?.postal_state || customer?.postal_address.state);
      form.setFieldValue('postalAddressPostcode', customer?.postal_postcode || customer?.postal_address.postcode);
    }
    setIsManualEditing(!isManualEditing);
  };

  return (
    <Section
      title={isTechnicianMode ? '' : 'Postal Address'}
      inline={inline}
      className={cx(isTechnicianMode && styles.billingAddress_techMode)}
    >
      <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
        <Space.$
          direction={Space.Direction.Vertical}
          size={Space.Size.Middle}
          className={styles.billingAddress__billingAddress}
        >
          {!isTechnicianMode && (
            <Switchbox.$
              dataTestId="billing-address"
              initialValue={isAddressSame}
              name="sameBillingAddress"
              label="Same as Above"
              rules={[]}
              onChange={(val) => {
                setIsAddressSame(val);
              }}
            />
          )}

          {!isAddressSame && (
            <Space.$
              direction={Space.Direction.Vertical}
              size={Space.Size.Middle}
              className={styles.billingAddress__address}
            >
              <Space.$
                direction={Space.Direction.Horizontal}
                size={Space.Size.Middle}
                className={styles.billingAddress__addressStreet1}
              >
                <Space.$
                  direction={Space.Direction.Horizontal}
                  compact
                  fullWidth
                  align={Space.Align.End}
                  style={{ alignItems: 'flex-end', position: 'relative' }}
                >
                  <AddressLookup
                    name="postalAddressLine1"
                    placeholder={'Street Address Line 1'}
                    onAddressSearch={(value: string) => {
                      predictAddress({ value, type: AddressType.Postal });
                    }}
                    onAddressSelect={handlePredictionSelect}
                    initialValue={
                      isTechnicianMode
                        ? customer?.postal_address_line1
                        : customer?.postal_address?.postal_address_line_1
                    }
                    label={isTechnicianMode ? ' ' : 'Street Address:'}
                    addressOptions={predictedBillingAddress?.suggest?.map((item: JsonDataItem) => {
                      return { label: item.address, value: item.id };
                    })}
                    className={styles.billingAddress__formItem_vertical}
                    dataTestId="postal-address-line-1"
                    isManual={isManualEditing}
                  />
                  <Button.$
                    onClick={handleEditingMode}
                    dataTestId="edit-switcher"
                    style={{ position: 'absolute', left: '100%', marginLeft: -1, top: isTechnicianMode ? 0 : 'auto' }}
                  >
                    {isManualEditing ? (
                      <>
                        <SearchOutlined /> Use lookup
                      </>
                    ) : (
                      <>
                        <EditOutlined /> Enter manually
                      </>
                    )}
                  </Button.$>
                </Space.$>
              </Space.$>

              {!isEmptyFieldsHidden && (
                <>
                  <Space.$ fullWidth stretch className={styles.billingAddress__addressStreet2}>
                    <InputText.$
                      dataTestId="postal-address-line-2"
                      name="postalAddressLine2"
                      className={styles.billingAddress__formItem_vertical}
                      fullWidth
                      placeholder="Street Address Line 2"
                      label={isTechnicianMode ? ' ' : undefined}
                      initialValue={
                        isTechnicianMode
                          ? customer?.postal_address_line2
                          : customer?.postal_address?.postal_address_line_2
                      }
                      disabled={isTechnicianMode && !isManualEditing}
                    />
                  </Space.$>
                  <Space.$
                    size={Space.Size.Middle}
                    className={styles.billingAddress__addressSuburbDetails}
                    direction={Space.Direction.Vertical}
                  >
                    <Space.$
                      direction={Space.Direction.Horizontal}
                      size={Space.Size.Middle}
                      className={styles.billingAddress__addressSuburb}
                    >
                      <AddressLookup
                        name="postalAddressSuburb"
                        placeholder={'Suburb'}
                        onAddressSearch={(value: string) => {
                          suburbLookup({ value, type: AddressType.Postal });
                        }}
                        onAddressSelect={handleSuburbSelect}
                        initialValue={
                          isTechnicianMode ? customer?.postal_suburb : customer?.postal_address?.postal_suburb
                        }
                        label={isTechnicianMode ? ' ' : 'Suburb and State:'}
                        addressOptions={suggestedSuburbs?.map((item: JsonDataItem) => {
                          return { label: `${item.suburb}, ${item.postcode}`, value: item.ID };
                        })}
                        className={styles.billingAddress__formItem_vertical}
                        dataTestId="postal-suburb"
                      />
                    </Space.$>
                    <Space.$
                      direction={Space.Direction.Horizontal}
                      size={Space.Size.Middle}
                      className={styles.billingAddress__addressSuburb}
                    >
                      <InputText.$
                        dataTestId="postal-address-state"
                        name="postalAddressState"
                        className={cx(styles.billingAddress__formItem_vertical, styles.billingAddress__addressState)}
                        fullWidth
                        label=" "
                        placeholder="State"
                        initialValue={
                          isTechnicianMode ? customer?.postal_state : customer?.postal_address?.postal_state
                        }
                        readonly
                        disabled={isTechnicianMode}
                      />
                      <InputText.$
                        dataTestId="postal-address-postcode"
                        name="postalAddressPostcode"
                        className={cx(styles.billingAddress__formItem_vertical, styles.billingAddress__addressPostcode)}
                        fullWidth
                        label=" "
                        placeholder="Postcode"
                        initialValue={
                          isTechnicianMode ? customer?.postal_postcode : customer?.postal_address?.postal_postcode
                        }
                        readonly
                        disabled={isTechnicianMode}
                      />
                    </Space.$>
                  </Space.$>
                </>
              )}
            </Space.$>
          )}
        </Space.$>
      </Space.$>
    </Section>
  );
};
